import * as React from 'react';
import { WorkspaceButton } from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import {
  useKbSegments,
  useKbSource,
  useKbSources,
  type KbSegment,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Chip, Empty, FieldLabel, Linkish, Quote, smallControl } from '../kb-bits';
import { formatDate } from '../kb-logic';

/**
 * A capture as stored (E13-F04 §3.1 `GET /versions/{id}/segments`, curators
 * only): its passages in order, under their headings or pages. A curator
 * picks a passage by selecting words inside one passage, or takes a whole
 * passage; the server then checks the quote against the capture (rule 7).
 * It opens in place, never over the page.
 */

export interface PickedPassage {
  sourceVersionId: string;
  segmentId: string;
  /** Code-point offsets in the segment, as the API takes them. */
  start: number;
  end: number;
  text: string;
}

const cp = (s: string) => Array.from(s).length;

function where(segment: KbSegment): string | null {
  const l = segment.locator;
  if (l.kind === 'web_block') {
    const path = (l.heading_path ?? []).filter((h) => h.trim());
    return path.length ? path.join(' › ') : null;
  }
  if (l.kind === 'page' && l.page !== undefined) return `Page ${l.page}`;
  if (l.kind === 'sheet_row') return `Sheet “${l.sheet ?? ''}”, row ${l.row ?? ''}`;
  if (l.kind === 'section') return l.section ? `Section “${l.section}”` : null;
  return null;
}

/** The selected words inside one passage element, as offsets into its text. */
function selectionIn(el: HTMLElement, text: string): { start: number; end: number } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return null;
  const before = document.createRange();
  before.selectNodeContents(el);
  before.setEnd(range.startContainer, range.startOffset);
  let a = before.toString().length;
  let b = a + range.toString().length;
  while (a < b && /\s/.test(text[a] ?? '')) a += 1;
  while (b > a && /\s/.test(text[b - 1] ?? '')) b -= 1;
  if (b <= a) return null;
  return { start: cp(text.slice(0, a)), end: cp(text.slice(0, b)) };
}

export function CaptureView({
  versionId,
  actionLabel,
  onPick,
  capturedAt,
}: {
  versionId: string;
  actionLabel: string;
  onPick: (p: PickedPassage) => void;
  capturedAt?: string | null;
}) {
  const segments = useKbSegments(versionId);
  const [picked, setPicked] = React.useState<PickedPassage | null>(null);

  if (segments.isPending) return <Spinner />;
  if (segments.isError) return <Empty small>The capture could not be read.</Empty>;
  const rows = segments.data.segments;
  if (rows.length === 0) return <Empty small>This capture has no text.</Empty>;

  const take = (segment: KbSegment, range: { start: number; end: number } | null) => {
    const chars = Array.from(segment.text);
    const r = range ?? { start: 0, end: chars.length };
    setPicked({
      sourceVersionId: versionId,
      segmentId: segment.id,
      start: r.start,
      end: r.end,
      text: chars.slice(r.start, r.end).join(''),
    });
  };

  let lastWhere: string | null = null;
  return (
    <div className="mt-2.5">
      {capturedAt ? (
        <div className="mb-1.5 text-meta text-ink-500">
          As captured {formatDate(capturedAt)}. Select words inside one passage, or take a whole
          passage.
        </div>
      ) : null}
      <div
        className="max-h-[420px] overflow-y-auto rounded-card border border-ink-100 bg-paper-0 px-4 py-3"
        data-testid="capture-view"
      >
        {rows.map((s) => {
          const w = where(s);
          const head = w && w !== lastWhere ? w : null;
          lastWhere = w;
          return (
            <div key={s.id} className="group py-1.5">
              {head ? (
                <div className="mb-1 mt-2 text-meta font-semibold text-ink-700">{head}</div>
              ) : null}
              <p
                className={cn(
                  'm-0 font-serif text-[15px] leading-6 text-ink-900',
                  s.changed_from_previous && 'border-l-2 border-accent-amber-500 pl-2.5',
                )}
              >
                {s.changed_from_previous ? (
                  <Chip tone="amber" className="mr-1.5 align-[2px] font-sans">
                    Changed
                  </Chip>
                ) : null}
                <span
                  data-segment={s.id}
                  className="selection:bg-cite-highlight"
                  onMouseUp={(e) => {
                    const range = selectionIn(e.currentTarget, s.text);
                    if (range) take(s, range);
                  }}
                >
                  {s.text}
                </span>
                <Linkish
                  tone="quiet"
                  className="ml-2 align-baseline font-sans opacity-0 focus:opacity-100 group-hover:opacity-100"
                  onClick={() => take(s, null)}
                >
                  Use this passage
                </Linkish>
              </p>
            </div>
          );
        })}
      </div>
      {picked ? (
        <div className="mt-2.5 rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5">
          <div className="text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
            Selected passage
          </div>
          <Quote text={picked.text} />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <WorkspaceButton variant="primary" onClick={() => onPick(picked)}>
              {actionLabel}
            </WorkspaceButton>
            <WorkspaceButton variant="tertiary" onClick={() => setPicked(null)}>
              Clear
            </WorkspaceButton>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Pick a source of the vendor, then a passage of its latest capture. */
export function PassagePicker({
  vendorId,
  actionLabel,
  onPick,
}: {
  vendorId: string;
  actionLabel: string;
  onPick: (p: PickedPassage) => void;
}) {
  const sources = useKbSources(vendorId);
  const usable = (sources.data?.sources ?? []).filter(
    (s) => s.status !== 'retired' && s.kind !== 'avero_kb' && s.current_version_no !== null,
  );
  const [sourceId, setSourceId] = React.useState<string>('');
  const source = useKbSource(sourceId || null);
  const versionId = source.data?.source.current_version_id ?? null;
  const id = React.useId();

  if (sources.isPending) return <Spinner />;
  if (usable.length === 0) {
    return (
      <Empty small className="mt-2.5">
        Nothing captured for this vendor yet. Add a web page or a document under Sources first.
      </Empty>
    );
  }
  return (
    <div className="mt-2.5">
      <FieldLabel htmlFor={id}>Source</FieldLabel>
      <select
        id={id}
        className={`${smallControl} max-w-full pr-7`}
        value={sourceId}
        onChange={(e) => setSourceId(e.target.value)}
      >
        <option value="" disabled>
          Pick a page or document
        </option>
        {usable.map((s) => (
          <option key={s.id} value={s.id}>
            {s.title ?? s.url ?? 'Untitled'}
          </option>
        ))}
      </select>
      {versionId ? (
        <CaptureView
          versionId={versionId}
          actionLabel={actionLabel}
          onPick={onPick}
          capturedAt={source.data?.versions[0]?.captured_at ?? null}
        />
      ) : sourceId && source.isPending ? (
        <div className="mt-2.5">
          <Spinner />
        </div>
      ) : null}
    </div>
  );
}
